import { createPortal } from "react-dom"
import { useEffect, useMemo, useState } from "react"
import type { Client } from "@xmpp/client"
import { Element } from "@xmpp/xml"
import AdHocCommands from "@/api/adHocCommands"
import { useAppSelector } from "@/reducers/hooks"
import { driversProfiles } from "@/reducers/profilesSliceSelectors"
import { DateRangePicker } from "@/components/ui/DateRangePicker"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"

interface DeliveryOrder {
  id: number
  driver_jid: string
  delivery_order_timestamp: string
  first_stop_id: string | null
  last_stop_id: string | null
  stop_data: string | null
  is_fulfilled: boolean
  created_at: string
  odometer_at_create: number | null
}

function parseStopIds(stopData: string | null): string[] {
  if (!stopData) return []
  try {
    const parsed = JSON.parse(stopData)
    return (parsed.stops ?? []).map((s: { id: string }) => s.id)
  } catch {
    return []
  }
}

type Tab = "active" | "completed" | "all"
type SortDir = "asc" | "desc"

interface Props {
  client: Client
}

function parseResponse(result: Element): DeliveryOrder[] {
  const jsonElement = result
    .getChild("command", "http://jabber.org/protocol/commands")
    ?.getChild("json", "urn:xmpp:json:0")
  if (!jsonElement) return []
  try {
    return JSON.parse(jsonElement.text()).orders ?? []
  } catch {
    return []
  }
}

function defaultDateRange() {
  const to = new Date()
  to.setDate(to.getDate() + 1)
  to.setHours(23, 59, 59, 999)
  const from = new Date()
  from.setDate(from.getDate() - 30)
  from.setHours(0, 0, 0, 0)
  return { from, to }
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString(getIntlLocale("pl-PL"))
}

function StatusBadge({ fulfilled }: { fulfilled: boolean }) {
  const { t } = useTranslation()
  if (fulfilled) {
    return (
      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        {t("orders.deliveryOrdersView.status.completed")}
      </span>
    )
  }
  return (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
      {t("orders.deliveryOrdersView.status.inProgress")}
    </span>
  )
}

export function DeliveryOrdersView({ client }: Props) {
  const { t } = useTranslation()
  const [allOrders, setAllOrders] = useState<DeliveryOrder[]>([])
  const [isLoading, setIsLoading] = useState(true)
  // The message is translated at render, so a language switch updates it.
  const [fetchFailed, setFetchFailed] = useState(false)
  const [tab, setTab] = useState<Tab>("active")
  const [selectedDriverJid, setSelectedDriverJid] = useState<string>("all")
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(
    defaultDateRange,
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortDir, setSortDir] = useState<SortDir>("desc")

  const profiles = useAppSelector(driversProfiles) ?? []

  const driverNameMap = useMemo(() => {
    const map = new Map<string, string>()
    profiles.forEach((p) => {
      map.set(p.jid, p.name)
    })
    return map
  }, [profiles])

  function resolveDriverName(driverJid: string): string {
    const local = driverJid.split("@")[0]
    for (const [jid, name] of driverNameMap) {
      if (jid.startsWith(local + "@")) return name
    }
    return driverJid
  }

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setFetchFailed(false)
    AdHocCommands.getDeliveryOrders(client)
      .then((result) => {
        if (!cancelled) setAllOrders(parseResponse(result))
      })
      .catch(() => {
        if (!cancelled) setFetchFailed(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client])

  useEffect(() => {
    setCurrentPage(1)
  }, [tab, selectedDriverJid, dateRange])

  const filteredOrders = useMemo(() => {
    return allOrders.filter((o) => {
      if (selectedDriverJid !== "all" && o.driver_jid !== selectedDriverJid)
        return false
      const created = new Date(o.created_at)
      if (created < dateRange.from || created > dateRange.to) return false
      return true
    })
  }, [allOrders, selectedDriverJid, dateRange])

  const tabOrders = useMemo(() => {
    let orders = filteredOrders
    if (tab === "active") orders = filteredOrders.filter((o) => !o.is_fulfilled)
    if (tab === "completed")
      orders = filteredOrders.filter((o) => o.is_fulfilled)
    return [...orders].sort((a, b) => {
      const diff =
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      return sortDir === "asc" ? diff : -diff
    })
  }, [filteredOrders, tab, sortDir])

  const totalPages = Math.max(1, Math.ceil(tabOrders.length / rowsPerPage))
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage
    return tabOrders.slice(start, start + rowsPerPage)
  }, [tabOrders, currentPage, rowsPerPage])

  // Build dropdown from actual order driver_jids — guarantees exact match on filter.
  // Display name resolved from profiles by local-part match.
  const uniqueDriverOptions = useMemo(() => {
    const jids = [...new Set(allOrders.map((o) => o.driver_jid))].sort()
    return jids
      .map((jid) => ({ jid, name: resolveDriverName(jid) }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [allOrders, driverNameMap])

  const { activeCount, completedCount, totalCount } = useMemo(
    () => ({
      activeCount: filteredOrders.filter((o) => !o.is_fulfilled).length,
      completedCount: filteredOrders.filter((o) => o.is_fulfilled).length,
      totalCount: filteredOrders.length,
    }),
    [filteredOrders],
  )

  const content = (
    <div className="fixed inset-0 left-[49px] z-50 flex flex-col bg-background">
      <div className="flex h-full flex-col overflow-hidden p-6">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">
            {t("orders.deliveryOrdersView.title")}
          </h1>
          <div className="flex items-center gap-3">
            <select
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              value={selectedDriverJid}
              onChange={(e) => setSelectedDriverJid(e.target.value)}
            >
              <option value="all">
                {t("orders.deliveryOrdersView.allDrivers")}
              </option>
              {uniqueDriverOptions.map(({ jid, name }) => (
                <option key={jid} value={jid}>
                  {name}
                </option>
              ))}
            </select>
            <DateRangePicker
              dateRange={dateRange}
              setDateRange={(r) => r && setDateRange(r)}
              maxDays={365}
              showClearButton={false}
            />
          </div>
        </div>

        {isLoading && (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            {t("orders.deliveryOrdersView.loading")}
          </div>
        )}

        {fetchFailed && (
          <div className="flex flex-1 items-center justify-center text-destructive">
            {t("orders.deliveryOrdersView.fetchFailed")}
          </div>
        )}

        {!isLoading && !fetchFailed && (
          <>
            {/* Stats */}
            <div className="mb-4 grid grid-cols-3 gap-4">
              <div
                data-testid="stat-active"
                className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center"
              >
                <div className="text-3xl font-bold text-amber-600">
                  {activeCount}
                </div>
                <div className="text-xs font-medium uppercase text-amber-800">
                  {t("orders.deliveryOrdersView.stats.inProgress")}
                </div>
              </div>
              <div
                data-testid="stat-completed"
                className="rounded-lg border border-green-200 bg-green-50 p-4 text-center"
              >
                <div className="text-3xl font-bold text-green-600">
                  {completedCount}
                </div>
                <div className="text-xs font-medium uppercase text-green-800">
                  {t("orders.deliveryOrdersView.stats.completed")}
                </div>
              </div>
              <div
                data-testid="stat-total"
                className="rounded-lg border border-violet-200 bg-violet-50 p-4 text-center"
              >
                <div className="text-3xl font-bold text-violet-600">
                  {totalCount}
                </div>
                <div className="text-xs font-medium uppercase text-violet-800">
                  {t("orders.deliveryOrdersView.stats.total")}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="mb-0 flex border-b border-border">
              {(
                [
                  {
                    key: "active",
                    label: t("orders.deliveryOrdersView.tabs.active", {
                      number: activeCount,
                    }),
                    testId: "tab-active",
                  },
                  {
                    key: "completed",
                    label: t("orders.deliveryOrdersView.tabs.completed", {
                      number: completedCount,
                    }),
                    testId: "tab-completed",
                  },
                  {
                    key: "all",
                    label: t("orders.deliveryOrdersView.tabs.all", {
                      number: totalCount,
                    }),
                    testId: "tab-all",
                  },
                ] as const
              ).map(({ key, label, testId }) => (
                <button
                  key={key}
                  data-testid={testId}
                  onClick={() => setTab(key)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    tab === key
                      ? "border-b-2 border-indigo-500 text-indigo-600"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto">
              {tabOrders.length === 0 ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  {t("orders.deliveryOrdersView.noOrders")}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 pt-3 font-medium">
                        {t("orders.deliveryOrdersView.columns.driver")}
                      </th>
                      <th className="pb-2 pr-4 pt-3 font-medium">
                        {t("orders.deliveryOrdersView.columns.stops")}
                      </th>
                      <th className="pb-2 pr-4 pt-3 font-medium">
                        <button
                          className="flex items-center gap-1 hover:text-foreground"
                          onClick={() =>
                            setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                          }
                        >
                          {t("orders.deliveryOrdersView.columns.date")}{" "}
                          {sortDir === "asc" ? "↑" : "↓"}
                        </button>
                      </th>
                      <th className="pb-2 pt-3 font-medium">
                        {t("orders.deliveryOrdersView.columns.status")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map((o) => {
                      const stopIds = parseStopIds(o.stop_data)
                      return (
                        <tr key={o.id} className="border-b hover:bg-muted/40">
                          <td className="py-2 pr-4 align-top">
                            {resolveDriverName(o.driver_jid)}
                          </td>
                          <td className="py-2 pr-4 text-muted-foreground">
                            {stopIds.length > 0 ? (
                              <ol className="list-inside list-decimal space-y-0.5">
                                {stopIds.map((id, i) => (
                                  <li key={i} className="text-xs">
                                    {id}
                                  </li>
                                ))}
                              </ol>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="py-2 pr-4 align-top text-muted-foreground">
                            {formatDate(o.created_at)}
                          </td>
                          <td className="py-2 align-top">
                            <StatusBadge fulfilled={o.is_fulfilled} />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {tabOrders.length > 0 && (
              <div className="flex items-center justify-between border-t border-border pt-3">
                <div className="text-xs text-muted-foreground">
                  {t("orders.deliveryOrdersView.showing", {
                    from: Math.min(
                      (currentPage - 1) * rowsPerPage + 1,
                      tabOrders.length,
                    ),
                    to: Math.min(currentPage * rowsPerPage, tabOrders.length),
                    total: tabOrders.length,
                  })}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">
                    {t("orders.deliveryOrdersView.rows")}
                  </span>
                  <select
                    className="rounded border border-input bg-background px-1 py-0.5 text-xs"
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                  >
                    {[10, 20, 50].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <span className="text-muted-foreground">
                    {t("orders.deliveryOrdersView.pageOf", {
                      current: currentPage,
                      total: totalPages,
                    })}
                  </span>
                  <button
                    className="rounded border border-input px-2 py-0.5 disabled:opacity-40"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    ‹
                  </button>
                  <button
                    className="rounded border border-input px-2 py-0.5 disabled:opacity-40"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
